import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import VideoCall from '../Icons/VideoCall';
import AppButton from '../../styles/common/AppButton';
import { PAINT_COLORS, REMOTE_MEMBER_STATUS } from '../../lib/constants';
import Badge from '../Parts/Badge';
import ColorSF2 from '../../styles/common/ColorSF2';

const Dialog = styled.div`
  overflow-y: auto;
  width: 340px;
  max-height: 520px;
  display: flex;
  flex-direction: column;
  background-color: ${ColorSF2.white};
  color: ${ColorSF2.black};
  @media (max-height: 530px) {
    max-height: 310px;
  }
`;
const MemberWrapper = styled.div`
  height: 52px;
  padding: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${ColorSF2.white};
  border: 1px solid ${ColorSF2.mediumGray};
`;
const MemberInfoWrapper = styled.div``;
const MemberNameWrapper = styled.div`
  display: flex;
  align-items: center;
`;
const MemberName = styled.div`
  margin-left: 4px;
  width: 230px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 14px;
  font-weight: 600;
`;
const Remotestatus = styled.div`
  margin-top: 4px;
  font-size: 12px;
`;
const ActionButton = styled.div``;

/**
 * 通話グループのメンバーの一覧を表示するダイアログ
 * - メンバーのステータスが表示される。(例: 参加中、呼び出し中)
 * - メンバーのステータスに応じて以下のアクションを行える
 *   - 再通知
 *   - ビデオの共有
 *
 * @component
 * @param {Object} props
 * @param {Object} props.userInfo - ユーザーの情報
 * @param {string} props.appRoomId - アプリのルームID
 * @param {Array} props.members - 通話グループのメンバーのリスト
 * @param {string} props.currentSharerParticipantId - 現在のビデオ共有を行っている参加者ID
 * @param {Function} props.shareVideo - ビデオを共有する関数
 * @param {Function} props.callAgain - 再度呼び出しを行う関数
 * @param {Function} props.close - ダイアログを閉じる際に呼ばれるコールバック関数
 * @returns {ReactElement} MembersDialog コンポーネント
 */
const MembersDialog = ({
  userInfo,
  appRoomId,
  members,
  currentSharerParticipantId,
  shareVideo,
  callAgain,
  close,
}) => {
  const sortedMembers = members.sort((a, b) => (a.index < b.index ? -1 : 1));
  return (
    <Dialog role="presentation" onClick={e => e.stopPropagation()}>
      {sortedMembers.map(member => {
        const {
          operatorId,
          name,
          status,
          device,
          participantId,
          index: colorIndex,
          groupName,
        } = member;
        const isMe = operatorId === userInfo.operatorId;
        const paintColor =
          (PAINT_COLORS.length > colorIndex && PAINT_COLORS[colorIndex]) ||
          PAINT_COLORS[0];
        const key = `RemoteMember-${operatorId}`;
        const showShareVideoButton =
          status === REMOTE_MEMBER_STATUS.joined && device && device.deviceId;
        const showRecallButton =
          status ===
          (REMOTE_MEMBER_STATUS.wait || status === REMOTE_MEMBER_STATUS.left);
        return (
          <MemberWrapper id={key} key={key}>
            <MemberInfoWrapper>
              <MemberNameWrapper>
                <Badge showAlways bgColor={paintColor} />
                <MemberName>{`${name}${
                  (groupName && `(${groupName})`) || ''
                }`}</MemberName>
              </MemberNameWrapper>
              <Remotestatus>
                <FormattedMessage id={`remotePage.members.status.${status}`} />
              </Remotestatus>
            </MemberInfoWrapper>
            {!isMe && (
              <ActionButton>
                {showShareVideoButton && (
                  <AppButton
                    small
                    className="button"
                    bgColor={ColorSF2.brightBlue}
                    disabled={currentSharerParticipantId === participantId}
                    onClick={() => {
                      shareVideo(participantId);
                      close();
                    }}
                  >
                    <VideoCall />
                  </AppButton>
                )}
                {showRecallButton && (
                  <AppButton
                    small
                    className="button"
                    bgColor={ColorSF2.alertRed}
                    onClick={() => {
                      callAgain({ appRoomId, operatorId });
                      close();
                    }}
                  >
                    <FormattedMessage id="remotePage.members.button.call" />
                  </AppButton>
                )}
              </ActionButton>
            )}
          </MemberWrapper>
        );
      })}
    </Dialog>
  );
};

MembersDialog.propTypes = {
  userInfo: PropTypes.object.isRequired,
  appRoomId: PropTypes.number.isRequired,
  members: PropTypes.array.isRequired,
  currentSharerParticipantId: PropTypes.string.isRequired,
  shareVideo: PropTypes.func.isRequired,
  callAgain: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
};

export default MembersDialog;
